import { useEffect, useState, type ReactNode } from "react";
import { API_BASE_URL } from "./constants";

interface TopBanner {
  type: "loading" | "error";
  message: string;
}

interface OrderPdfPanelProps {
  isDragging?: boolean;
  onDrop?: React.DragEventHandler<HTMLDivElement>;
  onDragOver?: React.DragEventHandler<HTMLDivElement>;
  onDragEnter?: React.DragEventHandler<HTMLDivElement>;
  onDragLeave?: React.DragEventHandler<HTMLDivElement>;
  fileInputRef?: React.RefObject<HTMLInputElement | null>;
  onFileChange?: React.ChangeEventHandler<HTMLInputElement>;
  showDropZone?: boolean;
  pdfDisplayUrl?: string | null;
  uploadedFilePublicId?: string | null;
  sticky?: boolean;
  stickyMaxHeight?: boolean;
  stickyTop?: number;
  headerActions?: ReactNode;
  topBanners?: TopBanner[];
  parseStatus?: "parsing" | "done" | "error" | null;
  parseError?: string | null;
  isLoadingPdf?: boolean;
  showNotFound?: boolean;
}

function useUploadedFilePdfUrl(uploadedFilePublicId: string | null | undefined) {
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    if (!uploadedFilePublicId) {
      setPdfBlobUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      setIsLoading(false);
      setLoadFailed(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setLoadFailed(false);
    fetch(`${API_BASE_URL}/api/v1/uploaded-files/${uploadedFilePublicId}/download`, {
      credentials: "include",
    })
      .then(async (res) => {
        if (cancelled) return;
        if (!res.ok) {
          setLoadFailed(true);
          return;
        }
        const blob = await res.blob();
        if (cancelled) return;
        setPdfBlobUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(blob);
        });
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [uploadedFilePublicId]);

  useEffect(() => {
    return () => {
      setPdfBlobUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, []);

  return { pdfBlobUrl, isLoading, loadFailed };
}

export function OrderPdfPanel({
  isDragging = false,
  onDrop,
  onDragOver,
  onDragEnter,
  onDragLeave,
  fileInputRef,
  onFileChange,
  showDropZone = false,
  pdfDisplayUrl,
  uploadedFilePublicId,
  sticky = true,
  stickyMaxHeight = false,
  stickyTop = 16,
  headerActions,
  topBanners,
  parseStatus,
  parseError,
  isLoadingPdf,
  showNotFound,
}: OrderPdfPanelProps) {
  const uploadedPdf = useUploadedFilePdfUrl(pdfDisplayUrl ? null : uploadedFilePublicId);
  const displayUrl = pdfDisplayUrl ?? uploadedPdf.pdfBlobUrl;
  const waitingForUploadedPdf =
    !!uploadedFilePublicId && !displayUrl && !uploadedPdf.loadFailed && !showDropZone;
  const loadingPdf = isLoadingPdf || uploadedPdf.isLoading || waitingForUploadedPdf;
  const notFound =
    showNotFound ||
    (!loadingPdf &&
      !displayUrl &&
      !!uploadedFilePublicId &&
      !showDropZone &&
      uploadedPdf.loadFailed);

  return (
    <div
      style={{
        position: sticky ? "sticky" : "relative",
        ...(sticky ? { top: stickyTop } : {}),
        ...(stickyMaxHeight
          ? { maxHeight: `calc(100vh - ${stickyTop + 16}px)`, overflowY: "auto" }
          : {}),
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <div
        style={{
          background: "var(--bg-raised)",
          border: "0.5px solid var(--rule)",
          borderRadius: 3,
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "14px 18px",
            borderBottom: "0.5px solid var(--rule-soft)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "var(--bg-panel)",
          }}
        >
          <div className="eyebrow">注文書</div>
          {headerActions}
        </div>

        {/* Top banners (upload progress / errors) */}
        {topBanners?.map((b, i) => (
          <div
            key={i}
            role={b.type === "error" ? "alert" : undefined}
            style={{
              padding: "8px 14px",
              fontSize: 11,
              color: b.type === "error" ? "var(--danger)" : "var(--ink-3)",
              background: b.type === "error" ? "oklch(0.96 0.05 30)" : "oklch(0.97 0.01 250)",
              borderBottom: "0.5px solid var(--rule-softer)",
            }}
          >
            {b.message}
          </div>
        ))}

        {/* Drop zone (クリックでファイル選択ダイアログを開く) */}
        {showDropZone && (
          <div
            role="button"
            tabIndex={0}
            onClick={() => fileInputRef?.current?.click()}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                fileInputRef?.current?.click();
              }
            }}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragEnter={onDragEnter}
            onDragLeave={onDragLeave}
            style={{
              margin: 12,
              padding: "36px 24px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 12,
              border: `1.5px dashed ${isDragging ? "var(--accent)" : "var(--rule-soft)"}`,
              borderRadius: 3,
              background: isDragging ? "oklch(0.97 0.02 250)" : "transparent",
              transition: "background 0.15s, border-color 0.15s",
              cursor: "pointer",
            }}
          >
            <svg
              width="28"
              height="28"
              viewBox="0 0 28 28"
              fill="none"
              style={{ color: isDragging ? "var(--accent)" : "var(--ink-4)" }}
            >
              <rect
                x="4"
                y="4"
                width="20"
                height="20"
                rx="3"
                stroke="currentColor"
                strokeWidth="1.2"
              />
              <path
                d="M10 14h8M14 10v8"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinecap="round"
              />
            </svg>
            <div
              style={{
                fontSize: 12,
                color: isDragging ? "var(--accent)" : "var(--ink-3)",
                textAlign: "center",
                lineHeight: 1.6,
                transition: "color 0.15s",
              }}
            >
              {isDragging ? (
                "ここにドロップ"
              ) : (
                <>
                  クリックして選択 / ドラッグ&ドロップ
                  <br />
                  PDF を自動解析して入力欄に反映します。
                </>
              )}
            </div>
          </div>
        )}

        {/* Hidden file input */}
        {fileInputRef && onFileChange && (
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            style={{ display: "none" }}
            onChange={onFileChange}
          />
        )}

        {/* File content section */}
        {!showDropZone && (
          <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
            {parseStatus === "parsing" && (
              <div
                style={{
                  padding: "8px 14px",
                  fontSize: 11,
                  color: "var(--ink-3)",
                  background: "oklch(0.97 0.01 250)",
                  borderBottom: "0.5px solid var(--rule-softer)",
                }}
              >
                解析中…
              </div>
            )}
            {parseStatus === "error" && (
              <div
                style={{
                  padding: "8px 14px",
                  fontSize: 11,
                  color: "var(--danger)",
                  background: "oklch(0.96 0.05 30)",
                  borderBottom: "0.5px solid var(--rule-softer)",
                }}
              >
                解析エラー: {parseError}
              </div>
            )}
            {loadingPdf && (
              <div
                style={{
                  padding: "8px 14px",
                  fontSize: 11,
                  color: "var(--ink-3)",
                  background: "oklch(0.97 0.01 250)",
                  borderBottom: "0.5px solid var(--rule-softer)",
                }}
              >
                読み込み中…
              </div>
            )}
            {displayUrl && (
              <iframe
                src={displayUrl}
                title="注文書プレビュー"
                style={{
                  width: "100%",
                  height: "calc(100vh - 260px)",
                  minHeight: 480,
                  border: "none",
                  display: "block",
                }}
              />
            )}
            {notFound && (
              <div
                style={{
                  padding: "8px 14px",
                  fontSize: 11,
                  color: "var(--ink-3)",
                  borderBottom: "0.5px solid var(--rule-softer)",
                }}
              >
                ファイルが見つかりませんでした
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
